import React from 'react';
import { Container, Typography, Box, Link } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * IncidentPhotoEvidence — shows student-attached photo evidence on the incident (FEAT-016 #85).
 *
 * The backend returns `imageUrls` (short-lived presigned GET URLs) alongside the incident when the
 * viewer is authorized to see it (portalApi mints them at read time from the private
 * disclosure-incident-media bucket). We render thumbnails; clicking opens the full image in a new
 * tab. Renders nothing when there are no attachments. URLs expire (~5 min) — reopen the incident to
 * refresh; access is audited.
 */
const IncidentPhotoEvidence = ({ imageUrls, imageMeta }) => {
  const theme = useTheme();
  const urls = Array.isArray(imageUrls) ? imageUrls.filter(Boolean) : [];
  if (urls.length === 0) return null;

  // Compact photo metadata (capture time / device / dimensions) — evidentiary "additional info".
  const metaLine = (meta) => {
    if (!meta) return null;
    const parts = [];
    if (meta.capturedAt) parts.push(`Taken ${new Date(meta.capturedAt).toLocaleString()}`);
    const device = [meta.make, meta.model].filter(Boolean).join(' ');
    if (device) parts.push(device);
    if (meta.width && meta.height) parts.push(`${meta.width}\u00d7${meta.height}`);
    return parts.length ? parts.join('  \u00b7  ') : null;
  };
  const metas = Array.isArray(imageMeta) ? imageMeta : [];

  const style = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '60px',
      marginRight: '60px',
      marginTop: '40px',
      width: 'auto',
      paddingLeft: '0px !important',
      border: `1px solid ${theme.palette.custom?.gray || '#ccc'}`,
      borderRadius: '4px',
    },
    header: { borderBottom: `1px solid ${theme.palette.custom?.gray || '#ccc'}` },
    title: { fontSize: '20px', fontWeight: '700', marginTop: '20px', marginBottom: '20px', marginLeft: '20px' },
    body: { padding: '20px' },
    meta: { fontSize: '13px', color: theme.palette.text?.secondary || '#666', marginBottom: '12px' },
    grid: { display: 'flex', flexWrap: 'wrap', gap: '12px' },
    img: {
      width: '220px',
      height: '220px',
      objectFit: 'cover',
      borderRadius: '8px',
      border: `1px solid ${theme.palette.custom?.gray || '#ccc'}`,
      cursor: 'pointer',
    },
  };

  return (
    <Container sx={style.container}>
      <Container sx={style.header}>
        <Typography sx={style.title}>Photo Evidence</Typography>
      </Container>
      <Box sx={style.body}>
        {metaLine(metas[0]) ? (
          <Typography sx={{ ...style.meta, fontWeight: 600, color: theme.palette.text?.primary }}>
            {metaLine(metas[0])}
          </Typography>
        ) : null}
        <Typography sx={style.meta}>
          Attached by the reporter. Access is recorded; these links expire — reopen the incident to refresh.
        </Typography>
        <Box sx={style.grid}>
          {urls.map((u, i) => (
            <Link key={i} href={u} target="_blank" rel="noopener noreferrer">
              <Box component="img" src={u} alt={`Incident photo ${i + 1}`} sx={style.img} />
            </Link>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default IncidentPhotoEvidence;
